import React from 'react';
import {
  alpha,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import focusRing from '../../theme/focus';
import { RAISED_ELEVATION } from '../../theme/shadows';
import creditsRows from './creditsRows';

// Fixed, not measured: credits have no limit, so there is nothing to fill towards.
const RING_FILL_FRACTION = 1 / 3;

const RING_SIZE = 14;
const RING_STROKE = 2.25;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const CreditsRing = () => {
  const theme = useTheme();
  const center = RING_SIZE / 2;

  return (
    <svg width={RING_SIZE} height={RING_SIZE} viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`} aria-hidden>
      <circle
        cx={center}
        cy={center}
        r={RING_RADIUS}
        fill="none"
        stroke={theme.palette.divider}
        strokeWidth={RING_STROKE}
      />
      <circle
        cx={center}
        cy={center}
        r={RING_RADIUS}
        fill="none"
        stroke={alpha(theme.palette.emphasisText.main, 0.5)}
        strokeWidth={RING_STROKE}
        strokeLinecap="round"
        strokeDasharray={`${RING_CIRCUMFERENCE * RING_FILL_FRACTION} ${RING_CIRCUMFERENCE}`}
        transform={`rotate(-90 ${center} ${center})`}
      />
    </svg>
  );
};

const CreditsRow = ({
  label,
  value,
  loading,
}: {
  label: string;
  value: string;
  loading: boolean;
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
    <Typography variant="body1" color="text.secondary">
      {label}
    </Typography>
    {loading ? (
      <Typography variant="body1" color="text.secondary">
        Loading…
      </Typography>
    ) : (
      <Typography
        variant="body1"
        color="text.primary"
        sx={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </Typography>
    )}
  </Box>
);

type PinguCreditsMeterProps = {
  loading: boolean;
  conversationSpend: number | null;
  allConversationsSpend: number;
  sessionCount: number;
  sessionsCapped: boolean;
  onOpen: () => void;
  onClose: () => void;
};

const PinguCreditsMeter = ({
  loading,
  conversationSpend,
  allConversationsSpend,
  sessionCount,
  sessionsCapped,
  onOpen,
  onClose,
}: PinguCreditsMeterProps) => (
  <Tooltip
    describeChild
    placement="top-end"
    onOpen={onOpen}
    onClose={onClose}
    slotProps={{
      tooltip: {
        sx: {
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1.5,
          boxShadow: (theme) => theme.shadows[RAISED_ELEVATION],
          p: 3,
          width: 248,
          maxWidth: 'none',
          color: 'text.primary',
        },
      },
    }}
    title={(
      <Stack spacing={2}>
        <Typography variant="mediumTitle">
          AI credits
        </Typography>
        <Stack spacing={1}>
          {creditsRows({
            conversationSpend,
            allConversationsSpend,
            sessionCount,
            sessionsCapped,
          }).map(({ label, value }) => (
            <CreditsRow key={label} label={label} value={value} loading={loading} />
          ))}
        </Stack>
      </Stack>
    )}
  >
    <IconButton
      aria-label="AI credits"
      sx={{
        width: 24,
        height: 24,
        p: 0,
        borderRadius: 1.5,
        '&:focus-visible': { boxShadow: focusRing },
      }}
    >
      <CreditsRing />
    </IconButton>
  </Tooltip>
);

export default PinguCreditsMeter;
